import React from "react";
import { Form, Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import Select, { StylesConfig } from "react-select";

import { AppState } from "@/store/root.reducer";
import { FormArtwork } from "@/models/artwork.model";
import User from "@/models/user.model";

import "./new.artwork.css";

import {
  createArtworkApi,
  editArtworkApi,
} from "../../store/artworks/artworks.api";
import { ConfirmModal } from "./confirm-modal";
import ModelViewer from "../3d/model.viewer";
import { ArtworkOptimizationPanel } from "../artworks/ArtworkOptimizationPanel";
import { useArtworkOptimization } from "../artworks/useArtworkOptimization";
import { UploadDropzone } from "../molecules/UploadDropzone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan, faPaperclip, faCloudArrowUp } from "@fortawesome/free-solid-svg-icons";
import { Artwork } from "@/types/artworksData";
import {
  IMPORTED_METADATA_FIELDS,
  ImportedMetadataKey,
  PLACEHOLDER_IMPORT_MESSAGE,
  getImportedMetadata,
  isImageLikeUrl,
  isEuropeanaSource,
  isPlaceholderArtwork,
  normalizePseudoMoMMetadataEditValue,
} from "../artworks/importedMetadata";

export const ARTWORK_TAG_OPTIONS = [
  { value: "Fauvism", label: "Fauvism" },
  { value: "Surrealism", label: "Surrealism" },
  { value: "Abstract Expressionism", label: "Abstract Expressionism" },
  { value: "Pop Art", label: "Pop Art" },
  { value: "Op Art", label: "Op Art" },
  { value: "Minimalism", label: "Minimalism" },
  { value: "Photorealism", label: "Photorealism" },
  { value: "Street Art and Graffiti", label: "Street Art and Graffiti" },
  { value: "Baroque", label: "Baroque" },
  { value: "Neo-Expressionism", label: "Neo-Expressionism" },
  { value: "Digital Art", label: "Digital Art" },
  { value: "Conceptual Art", label: "Conceptual Art" },
  { value: "Rococo", label: "Rococo" },
  { value: "Neoclassicism", label: "Neoclassicism" },
  { value: "Romaticism", label: "Romaticism" },
  { value: "Realism", label: "Realism" },
  { value: "Impressionism", label: "Impressionism" },
  { value: "Post-Impressionism", label: "Post-Impressionism" },
  { value: "Expressionism", label: "Expressionism" },
  { value: "Knowledge", label: "Knowledge" },
  { value: "Infotainment", label: "Infotainment" },
  { value: "Creativity", label: "Creativity" },
  { value: "Curation", label: "Curation" },
];

export interface TagOption {
  readonly value: string;
  readonly label: string;
}

const emptyArtwork = {
  title: "",
  artistName: "",
  dateofcreation: "",
  description: [""],
  height: "",
  materials: "",
  attachments: [],
  tags: [],
  artworkType: "image",
  artType: "DIGITAL",
  physical: false,
  physicalMeta: {
    format: "",
    materials: "",
    dateOfCreation: null,
  },
  nft: null,
};
const BASE_API = process.env.REACT_APP_BASE_API;

type ModalProps = {
  show: boolean;
  onCreatedHandler: (edited: boolean, artworkPayload: Artwork | undefined) => void;
  onSuccess: (msg: string) => void;
  onFailed: (msg: string) => void;
  artworkToEdit?: any;
  inline?: boolean;
  hideInlineHeader?: boolean;
  hideInlinePreview?: boolean;
  enableAssetReplacement?: boolean;
  allowedImportedMetadataKeys?: ImportedMetadataKey[];
  pseudoMomLimitedMetadataEdit?: boolean;
  inlinePresentation?: "compact" | "modal";
  initialOptimizationJobId?: string | null;
  onOptimizationRestartPrepared?: () => void;
};

export const NewArtworkModal = ({
  show,
  onCreatedHandler,
  onSuccess,
  onFailed,
  artworkToEdit,
  inline = false,
  hideInlineHeader = false,
  hideInlinePreview = false,
  enableAssetReplacement,
  allowedImportedMetadataKeys,
  pseudoMomLimitedMetadataEdit = false,
  inlinePresentation = "compact",
  initialOptimizationJobId,
  onOptimizationRestartPrepared,
}: ModalProps) => {
  const tagStyles: StylesConfig<TagOption, true> = {
    control: (styles) => ({ ...styles, backgroundColor: "white" }),
    option: (styles, { isDisabled }) => {
      const color = "#000000";
      return {
        ...styles,
        color: color,
        cursor: isDisabled ? "not-allowed" : "default",
        ":active": {
          ...styles[":active"],
          backgroundColor: color,
        },
      };
    },
  };

  const [ edited, setEdited ] = React.useState<boolean>(false);
  const [showConfirm, setShowConfirm] = React.useState<boolean>(false);

  const [loading, setLoading] = React.useState<boolean>(false);
  const previewObjectUrlRef = React.useRef<string | null>(null);
  const [artworkFile, setArtworkFile] = React.useState<{ file: any, type: string }>(() => {
    if (artworkToEdit && artworkToEdit.artworkData?.dataUrl) {
      return { file: artworkToEdit.artworkData.dataUrl, type: artworkToEdit.type };
    }
    return { file: null, type: "" };
  });
  const inputFile = React.useRef(null);

  // Stable 3D preview refs to prevent re-init on each render
  const modelPreviewContainerRef = React.useRef<HTMLDivElement | null>(null);
  const modelViewerRef = React.useRef<ModelViewer | null>(null);
  const [isDraggingOverUpload, setIsDraggingOverUpload] = React.useState<boolean>(false);
  // Refs to focus description inputs when adding new ones
  const firstDescriptionRef = React.useRef<HTMLTextAreaElement | null>(null);
  const extraDescriptionRefs = React.useRef<Array<HTMLTextAreaElement | null>>([]);
  const prevDescriptionCountRef = React.useRef<number>(0);
  const didInitDescriptionCountRef = React.useRef<boolean>(false);
  const physicalSectionRef = React.useRef<HTMLDivElement | null>(null);
  const placeholderEdit = isPlaceholderArtwork(artworkToEdit);
  const placeholderThumbnailPreview =
    placeholderEdit &&
    artworkFile.type === "3dmodel" &&
    typeof artworkFile.file === "string" &&
    isImageLikeUrl(artworkFile.file);

  React.useEffect(() => () => {
    modelViewerRef.current?.dispose?.();
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
  }, []);


  const setSelectedFilePreview = (file: File, artworkType: string) => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }

    const fileExt = file.name.split(".").pop()?.toLowerCase();
    if (artworkType === "3dmodel" && fileExt === "glb") {
      const objectUrl = URL.createObjectURL(file);
      previewObjectUrlRef.current = objectUrl;
      setArtworkFile({ file: objectUrl, type: artworkType });
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setArtworkFile({
        file: event.target?.result as any,
        type: artworkType,
      });
    };
    reader.readAsDataURL(file);
  };

  const handleSelectedFile = (file: File) => {
    const mimeType = file.type;
    let artworkType = "";
    if (mimeType?.startsWith('image/')) {
      artworkType = "image";
    } else if (mimeType?.startsWith('video/')) {
      artworkType = 'video';
    } else if (mimeType?.startsWith('audio/')) {
      artworkType = 'sound';
    } else {
      const fileExt = file.name.split(".").pop()?.toLowerCase();
      const allowedExtensions = ['gltf', 'glb', 'obj', 'fbx', 'dae', 'amf', 'hdr'];
      if (fileExt && allowedExtensions.includes(fileExt)) {
        artworkType = "3dmodel";
      } else {
        artworkType = 'unsupported';
      }
    }

    setArtwork(prev => ({
      ...prev,
      nft: file,
      artworkType: artworkType,
      physical: artworkType === "video" || artworkType === "sound" ? false : prev.physical
    }));
    void artworkOptimization.resetForNewFile();
    setSelectedFilePreview(file, artworkType);
    setNftTouched(true);
  };

  const handleSelectedAttachments = (files: FileList | File[]) => {
    const fileList = Array.from(files);
    setArtwork(prev => {
      const previous = Array.isArray(prev.attachments) ? prev.attachments : [];
      const merged = [...previous];
      for (const file of fileList) {
        const exists = previous.some(
          (f: File) =>
            f.name === file.name &&
            f.size === file.size &&
            (f as any).lastModified === (file as any).lastModified
        );
        if (!exists) {
          merged.push(file);
        }
      }
      return { ...prev, attachments: merged };
    });
  };

  // need to get user
  const user = useSelector((state: AppState) => state.auth.user as User);

  // Error state management (like artwork form)
  const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
  const [showPseudoMomMetadataEditor, setShowPseudoMomMetadataEditor] = React.useState<boolean>(false);
  
  // Track if fields have been modified to hide errors on typing
  const [titleTouched, setTitleTouched] = React.useState<boolean>(false);
  const [artistNameTouched, setArtistNameTouched] = React.useState<boolean>(false);
  const [descriptionTouched, setDescriptionTouched] = React.useState<boolean[]>([]);
  const [heightTouched, setHeightTouched] = React.useState<boolean>(false);
  const [materialsTouched, setMaterialsTouched] = React.useState<boolean>(false);
  const [nftTouched, setNftTouched] = React.useState<boolean>(false);
  const [artworkTypeTouched, setArtworkTypeTouched] = React.useState<boolean>(false);
  const [tagsTouched, setTagsTouched] = React.useState<boolean>(false);
  const [dateofcreationTouched, setDateofcreationTouched] = React.useState<boolean>(false);

  // Parse errors for field-specific display (like artwork form)
  const getFieldError = (fieldName: string) => {
    if (!errors[fieldName]) return null;
    
    // Hide errors if user has started typing in the field
    if (fieldName === 'title' && titleTouched) return null;
    if (fieldName === 'artistName' && artistNameTouched) return null;
    if (fieldName === 'description' && (Array.isArray(descriptionTouched) ? descriptionTouched.some(Boolean) : !!descriptionTouched)) return null;
    if (fieldName === 'height' && heightTouched) return null;
    if (fieldName === 'materials' && materialsTouched) return null;
    if (fieldName === 'data' && nftTouched) return null;
    if (fieldName === 'artworkType' && artworkTypeTouched) return null;
    if (fieldName === 'tags' && tagsTouched) return null;
    if (fieldName === 'dateofcreation' && dateofcreationTouched) return null;
    
    return errors[fieldName];
  };

  // Parse server errors to field-specific errors (like artwork form)
  const parseServerErrors = (error: any) => {
    const fieldErrors: { [key: string]: string } = {};
    
    if (!error) return fieldErrors;
    
    // Check if error has field-specific validation errors
    if (typeof error === 'object' && error) {
      // Handle validation errors object
      if (error.errors) {
        Object.keys(error.errors).forEach(field => {
          const fieldError = error.errors[field];
          if (typeof fieldError === 'string') {
            fieldErrors[field] = fieldError;
          } else if (fieldError.message) {
            fieldErrors[field] = fieldError.message;
          } else if (Array.isArray(fieldError)) {
            fieldErrors[field] = fieldError[0]; // Take first error if array
          }
        });
      }
      
      // Handle response.data structure (if error is nested in response)
      if (error.response && error.response.data) {
        return parseServerErrors(error.response.data);
      }
      
      // Handle direct field errors
      if (error.title) fieldErrors.title = typeof error.title === 'string' ? error.title : error.title.message || 'Invalid title';
      if (error.artistName) fieldErrors.artistName = typeof error.artistName === 'string' ? error.artistName : error.artistName.message || 'Invalid artist name';
      if (error.description) fieldErrors.description = typeof error.description === 'string' ? error.description : error.description.message || 'Invalid description';
      if (error.height) fieldErrors.height = typeof error.height === 'string' ? error.height : error.height.message || 'Invalid height';
      if (error.materials) fieldErrors.materials = typeof error.materials === 'string' ? error.materials : error.materials.message || 'Invalid materials';
      if (error.dateofcreation) fieldErrors.dateofcreation = typeof error.dateofcreation === 'string' ? error.dateofcreation : error.dateofcreation.message || 'Invalid date';
      if (error.data) fieldErrors.data = typeof error.data === 'string' ? error.data : error.data.message || 'Invalid artwork file';
      if (error.artworkType) fieldErrors.artworkType = typeof error.artworkType === 'string' ? error.artworkType : error.artworkType.message || 'Invalid artwork type';
      if (error.tags) fieldErrors.tags = typeof error.tags === 'string' ? error.tags : error.tags.message || 'Invalid tags';
      
      // Handle msg field (common in your app)
      if (error.msg && !fieldErrors.general) {
        fieldErrors.general = error.msg;
      }
    }
    
    // If it's a string error, try to parse as JSON
    if (typeof error === 'string') {
      try {
        const errorObj = JSON.parse(error);
        return parseServerErrors(errorObj);
      } catch {
        // If not JSON, return as general error
        fieldErrors.general = error;
      }
    }
    
    return fieldErrors;
  };

  // Reset touched states when new errors come in
  React.useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setTitleTouched(false);
      setArtistNameTouched(false);
      setDescriptionTouched([]);
      setHeightTouched(false);
      setMaterialsTouched(false);
      setNftTouched(false);
      setArtworkTypeTouched(false);
      setTagsTouched(false);
      setDateofcreationTouched(false);
    }
  }, [errors]);

  const __artwork = artworkToEdit ? artworkToEdit : emptyArtwork;

  const [artwork, setArtwork] = React.useState<FormArtwork>({
    ...__artwork,
    tags:
      typeof __artwork.tags === "string" && __artwork.tags !== ""
        ? __artwork.tags.split(",").map((i: string) => ({ label: i, value: i }))
        : [],
    format: __artwork.physical ? __artwork.physicalMeta.format : undefined,
    materials: __artwork.physical
      ? __artwork.physicalMeta.materials
      : undefined,
    dateofcreation: __artwork.physical && __artwork.physicalMeta.dateOfCreation
      ? new Date(__artwork.physicalMeta.dateOfCreation)
          .toISOString()
          .substring(0, 10)
      : undefined,
    user: user,
    artworkType: __artwork.type,
    height: __artwork.artworkData && __artwork.artworkData.height ? __artwork.artworkData.height : 100
  });
  const selectedArtworkFile = artwork.nft instanceof File ? artwork.nft : null;
  const artworkOptimization = useArtworkOptimization({
    show,
    isEditing: Boolean(artworkToEdit),
    initialJobId: initialOptimizationJobId,
    sourceFile: selectedArtworkFile,
    is3dModel: artwork.artworkType === "3dmodel",
    isPhysical: Boolean(artwork.physical),
    hasAttachments: Boolean(artwork.attachments?.length),
    draft: {
      title: String(artwork.title || ""),
      artistName: String(artwork.artistName || ""),
      description: Array.isArray(artwork.description) ? artwork.description.map(String) : [""],
      height: String(artwork.height ?? ""),
      materials: String(artwork.materials || ""),
      dateofcreation: String(artwork.dateofcreation || ""),
      artworkType: "3dmodel",
      tags: Array.isArray(artwork.tags)
        ? artwork.tags.map((tag: any) => String(tag?.value ?? tag)).filter(Boolean)
        : [],
    },
    onFailed,
    onDraftRestored: (restoredDraft) => {
      setArtwork((previous) => ({
        ...previous,
        title: restoredDraft.title,
        artistName: restoredDraft.artistName,
        description: restoredDraft.description,
        height: restoredDraft.height,
        materials: restoredDraft.materials,
        dateofcreation: restoredDraft.dateofcreation,
        artworkType: "3dmodel",
        tags: restoredDraft.tags.map((value) => ({ label: value, value })),
        physical: false,
        attachments: [],
      }));
    },
    onRestartPrepared: onOptimizationRestartPrepared,
    onFinalized: (createdArtwork) => {
      setEdited(true);
      onCreatedHandler(false, createdArtwork);
    },
    onSuccess,
  });
  const importedMetadata = getImportedMetadata((artwork as any).source);
  const showImportedMetadataEditor = Boolean(artworkToEdit && (pseudoMomLimitedMetadataEdit || isEuropeanaSource((artwork as any).source)));
  const shouldShowImportedMetadataFields = showImportedMetadataEditor && (!pseudoMomLimitedMetadataEdit || showPseudoMomMetadataEditor);

  React.useEffect(() => {
    modelViewerRef.current?.dispose?.();
    modelViewerRef.current = null;
    if (
      artworkOptimization.job ||
      !artworkFile.file ||
      artworkFile.type !== "3dmodel" ||
      placeholderThumbnailPreview ||
      !modelPreviewContainerRef.current
    ) {
      return;
    }

    const viewer = new ModelViewer();
    modelViewerRef.current = viewer;
    void Promise.resolve(viewer.init(modelPreviewContainerRef.current, artworkFile.file));
    return () => {
      viewer.dispose?.();
      if (modelViewerRef.current === viewer) modelViewerRef.current = null;
    };
  }, [artworkFile.file, artworkFile.type, placeholderThumbnailPreview, artworkOptimization.job?.jobId]);
  const allowedImportedMetadataKeySet = allowedImportedMetadataKeys ? new Set(allowedImportedMetadataKeys) : null;
  const visibleImportedMetadataFields = IMPORTED_METADATA_FIELDS.filter((field) =>
    field.key !== "shortDescription" &&
    (!allowedImportedMetadataKeySet || allowedImportedMetadataKeySet.has(field.key))
  );

  const updateImportedMetadataField = (field: ImportedMetadataKey, value: string) => {
    setArtwork((prev: any) => {
      const metadata = {
        ...(prev.source?.europeanaMetadata || {}),
        [field]: value,
      };
      const next: any = {
        ...prev,
        source: {
          ...(prev.source || {}),
          europeanaMetadata: metadata,
        },
      };
      const lang = next.edmProfile?.defaultLanguage || "en";
      if (field === "assetName") {
        next.title = value;
        next.name = value;
        next.edmProfile = {
          ...(next.edmProfile || {}),
          titles: value ? [{ property: "dc:title", value, lang }] : [],
        };
      }
      if (field === "shortDescription") {
        next.description = [value];
        next.edmProfile = {
          ...(next.edmProfile || {}),
          descriptions: value ? [{ property: "dc:description", value, lang }] : [],
        };
      }
      if (field === "assetType") {
        next.edmProfile = {
          ...(next.edmProfile || {}),
          types: value ? [{ property: "dc:type", value, lang }] : [],
        };
      }
      if (field === "category" || field === "subjects") {
        const subjects = String(metadata.subjects || "")
          .split(/\n|,/)
          .map((entry) => entry.trim())
          .filter(Boolean);
        const tagValues = Array.from(new Set([metadata.category, ...subjects].filter(Boolean)));
        next.tags = tagValues.map((tagValue) => ({ label: tagValue, value: tagValue }));
        next.edmProfile = {
          ...(next.edmProfile || {}),
          subjects: tagValues.map((tagValue) => ({ property: "dc:subject", value: tagValue, lang })),
        };
      }
      if (field === "managingInstitution") {
        next.edmProfile = {
          ...(next.edmProfile || {}),
          dataProvider: value,
        };
      }
      if (field === "country") {
        next.edmProfile = {
          ...(next.edmProfile || {}),
          country: value || null,
        };
      }
      if (field === "subNationalAuthority") {
        next.edmProfile = {
          ...(next.edmProfile || {}),
          subNationalAuthority: value || null,
        };
      }
      return next;
    });
  };

  React.useEffect(() => {
    setShowPseudoMomMetadataEditor(false);
  }, [artworkToEdit?._id]);

  // When physical properties become visible, scroll them into view
  React.useEffect(() => {
    const isPhysicalVisible =
      artwork.physical &&
      artwork.artworkType !== "sound" &&
      artwork.artworkType !== "video";
    if (isPhysicalVisible && physicalSectionRef.current) {
      try {
        physicalSectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      } catch {}
    }
  }, [artwork.physical, artwork.artworkType]);

  // Focus newly added description input
  React.useEffect(() => {
    const currentCount = Array.isArray(artwork.description)
      ? artwork.description.length
      : (artwork.description ? 1 : 0);
    if (!didInitDescriptionCountRef.current) {
      prevDescriptionCountRef.current = currentCount;
      didInitDescriptionCountRef.current = true;
      return;
    }
    if (currentCount > prevDescriptionCountRef.current) {
      const lastIndex = currentCount - 1;
      if (lastIndex === 0) {
        if (firstDescriptionRef.current) {
          const el = firstDescriptionRef.current;
          el.focus();
          try {
            const len = el.value?.length || 0;
            el.setSelectionRange(len, len);
          } catch {}
        }
      } else {
        const ref = extraDescriptionRefs.current[lastIndex - 1];
        if (ref) {
          ref.focus();
          try {
            const len = ref.value?.length || 0;
            ref.setSelectionRange(len, len);
          } catch {}
        }
      }
    }
    prevDescriptionCountRef.current = currentCount;
  }, [artwork.description]);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files[0];

    const mimeType = file.type;
    let artworkType = "";
    if (mimeType.startsWith('image/')) {
      artworkType = "image";
    } else if (mimeType.startsWith('video/')) {
      artworkType = 'video';
    } else if (mimeType.startsWith('audio/')) {
      artworkType = 'sound';
    } else {
      const fileExt = file.name.split(".").pop().toLowerCase();
      const allowedExtensions = ['gltf', 'glb', 'obj', 'fbx', 'dae', 'amf', 'hdr'];
      if (allowedExtensions.includes(fileExt.toLowerCase())) {
        artworkType = "3dmodel";
      } else {
        artworkType = 'unsupported';
      }
    }

    setArtwork({
      ...artwork, 
      nft: file, 
      artworkType: artworkType,
      physical: artworkType === "video" || artworkType === "sound" ? false : true
    });
    void artworkOptimization.resetForNewFile();
    setSelectedFilePreview(file, artworkType);
  };

  const removeAttachmentAtIndex = (index: number) => {
    setArtwork(prev => {
      const list = Array.isArray(prev.attachments) ? [...prev.attachments] : [];
      list.splice(index, 1);
      return { ...prev, attachments: list };
    });
  };

  const onPhysicalArt = (e: React.ChangeEvent<HTMLInputElement>) => {
    setArtwork({ ...artwork, physical: !artwork.physical });
  };

  const onChangeTags = (e) => {
    setArtwork({ ...artwork, tags: e });
  };

  const submitArtwork = async () => {
    try {
      setLoading(true);
      // Clear any existing errors
      setErrors({});
      
      // check edit or not
      if (artworkToEdit) {
        const editResponse = await editArtworkApi(artworkToEdit._id, artwork);
        if (!editResponse?.status) {
          throw editResponse;
        }
        const updatedArtwork = editResponse.artwork || editResponse.data?.artwork;
        onCreatedHandler(true, updatedArtwork);
        onSuccess("Artwork has been modified");
      } else {
        const createdArtwork = await createArtworkApi(artwork);
        onCreatedHandler(false, createdArtwork);

        // created artwork successfully
        setArtwork({
          ...emptyArtwork,
          physical: false,
          tags: [],
          user: user,
          description: [""],
        });
        setArtworkFile({ file: null, type: "" });
        if (inputFile.current) {
          inputFile.current.value = "";
          inputFile.current.type = "text";
          inputFile.current.type = "file";
        }
        onSuccess("Artwork has been created");
      }
      setEdited(true);
    } catch (error: any) {
      console.error("Error during submission:", error);
      
      // Parse and set field-specific errors
      const fieldErrors = parseServerErrors(error);
      setErrors(fieldErrors);
      
      // If there are field-specific errors, show them inline
      if (Object.keys(fieldErrors).length > 0) {
        const generalError = fieldErrors.general || "Please check the form for errors";
        onFailed(generalError);
      } else {
        // Fallback to original error handling
        const msg = error.response?.data?.msg;
        if (msg && typeof msg === "string") {
          onFailed(msg);
        } else {
          onFailed("Unknown error has been occured");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const createArtwork = async () => {
    if (artworkToEdit) {
      await submitArtwork();
    } else if (artwork.artworkType === "unsupported") {
      setShowConfirm(true);
    } else if (artworkOptimization.enabled && artworkOptimization.eligible) {
      await artworkOptimization.start();
      return;
    } else {
      await submitArtwork();
    }
  }

  const isAbleToReplaceAssets = artworkToEdit && (enableAssetReplacement ?? process.env.REACT_APP_ENABLE_ARTWORK_ASSET_REPLACE === 'true');
  const replacementAccept = artworkToEdit?.type === "3dmodel"
    ? ".gltf,.glb"
    : "image/*,video/*,audio/*,.gltf,.glb,.obj,.fbx,.dae,.amf,.hdr";
  const dialogTitle = artworkToEdit ? "Edit Artwork" : inline ? "Add Artwork" : "Create New Artwork";
  const submitLabel = artworkToEdit ? "Save Artwork" : "Create Artwork";
  const submissionWorking = loading || artworkOptimization.working;

  const inlineShellClass = inline
    ? (inlinePresentation === "modal" ? "new-artwork-inline-modal-shell" : "new-artwork-inline-shell")
    : "";
  const formContent = (
    <div id="create-item-modal" className={`modal-content-custom ${inlineShellClass}`}>
          <div
            id="modal-header"
            className={`modal-header-custom ${inline && hideInlineHeader ? "modal-header-hidden" : ""}`}
          >
              {(!inline || !hideInlineHeader) ? <h2>{dialogTitle}</h2> : <span aria-hidden="true" />}
              {!inline ? (
                <button 
                  id="exit-button-header" 
                  className="btn-close-custom"
                  onClick={() => onCreatedHandler(edited, undefined)}
                >
                    <svg
                      width="1em"
                      height="1em"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>
              ) : null}
          </div>
          <div id="modal-body" className="modal-body-custom">
            <form id="create-item-form">
                    <div className="row g-3 mb-3">
                      {!hideInlinePreview ? (
                      <div className="col-md-6" id="upload-file-group">
                            {artworkFile.file ? <>
                              <div className="preview-container">
                                {isAbleToReplaceAssets && <button 
                                  type="button" 
                                  aria-label="Replace file"
                                  onClick={() => {
                                    setArtworkFile({file: null, type: ""});
                                    // const input = document.getElementById('file-upload-input') as HTMLInputElement | null;
                                    // if (input) input.click();
                                  }}
                                  className="preview-delete-btn"
                                >
                                  <FontAwesomeIcon icon={faTrashCan} />
                                </button>}
                              {(artworkFile.file && (artworkFile.type === "image" || placeholderThumbnailPreview)) ? (
                                <img className="rounded my-2 media-preview" src={artworkFile.file} />
                              ) : (
                                <></>
                              )}
                              {artworkFile.file && artworkFile.type === "video" ? (
                                <video controls className="rounded my-2 media-preview" src={artworkFile.file} muted />
                              ) : (
                                <></>
                              )}
                              {artworkFile.file && artworkFile.type === "sound" && (
                                <audio controls className="rounded my-5">
                                  <source src={artworkFile.file} type={artworkFile.file.type}/>
                                </audio>
                              )}
                              {artworkFile.file && artworkFile.type === "3dmodel" && !placeholderThumbnailPreview && !artworkOptimization.job && (
                                <div
                                  ref={modelPreviewContainerRef}
                                  className="model-preview"
                                />
                              )}
                              {getFieldError('data') && (
                                <div className="text-danger small mt-1 text-center">
                                  {getFieldError('data')}
                                </div>
                              )}
                              </div>
                            </> : <>
                              <label htmlFor="file-upload" className="form-label">Upload File</label>
                            <UploadDropzone
                              className="upload-zone"
                              onFilesSelected={(files) => {
                                const list = Array.from(files);
                                if (list.length > 0) handleSelectedFile(list[0]);
                              }}
                              accept="image/*,video/*,audio/*,.gltf,.glb,.obj,.fbx,.dae,.amf,.hdr"
                              multiple={false}
                              ariaLabel="Drop a file here to upload"
                            >
                                <div>
                                    <FontAwesomeIcon
                                      icon={faCloudArrowUp}
                                      className="mb-2"
                                      style={{ width: "48px", height: "48px", color: "var(--brand-gray)", display: "block", margin: "0 auto" }}
                                    />
                                    <span className="upload-label">Upload a file</span>
                                    <span className="upload-hint"> or drag and drop</span>
                                    <p className="mt-1 mb-0 upload-subhint">Upload the original artwork file</p>
                                </div>
                            </UploadDropzone>
                            {getFieldError('data') && (
                              <div className="text-danger small mt-1">
                                {getFieldError('data')}
                              </div>
                            )}
                            </>}
                            {/* Hidden input is handled inside UploadDropzone */}
                        </div>
                      ) : null}
                        <div className={hideInlinePreview ? "col-12" : "col-md-6"} id="title-group">
                            <div>
                              <label htmlFor="artist-name" className="form-label">
                                Artist Name &nbsp;<span style={{ color: "red" }}>*</span>
                              </label>
                              <input 
                                id="artist-name" 
                                name="artist-name" 
                                className={`${getFieldError('artistName') ? 'is-invalid form-control' : 'form-control'}`}
                                type="text"
                                value={artwork.artistName}
                                onChange={(e) => {
                                  setArtwork({ ...artwork, artistName: e.target.value });
                                  setArtistNameTouched(true);
                                }}
                                placeholder="Enter artist name"
                                required
                              />
                              {getFieldError('artistName') && (
                                <div className="text-danger small mt-1">
                                  {getFieldError('artistName')}
                                </div>
                              )}
                            </div>
                            <label htmlFor="title" className="form-label">
                              Title &nbsp;<span style={{ color: "red" }}>*</span>
                            </label>
                            <input 
                              type="text" 
                              id="title" 
                              name="title" 
                              className={`${getFieldError('title') ? 'is-invalid form-control' : 'form-control'}`}
                              value={artwork.title}
                              onChange={(e) => {
                                if (showImportedMetadataEditor) {
                                  updateImportedMetadataField("assetName", e.target.value);
                                } else {
                                  setArtwork({ ...artwork, title: e.target.value });
                                }
                                setTitleTouched(true);
                              }}
                              placeholder="Enter title"
                              required
                            />
                            {getFieldError('title') && (
                              <div className="text-danger small mt-1">
                                {getFieldError('title')}
                              </div>
                            )}
                        </div>
                    </div>
                    <ArtworkOptimizationPanel
                      config={artworkOptimization.config}
                      eligible={artworkOptimization.eligible}
                      withinWorkerLimit={artworkOptimization.withinWorkerLimit}
                      enabled={artworkOptimization.enabled}
                      setEnabled={artworkOptimization.setEnabled}
                      available={artworkOptimization.available}
                      preset={artworkOptimization.preset}
                      blockedReason={artworkOptimization.blockedReason}
                      setPreset={artworkOptimization.setPreset}
                      job={artworkOptimization.job}
                      uploadPercent={artworkOptimization.uploadPercent}
                      error={artworkOptimization.error}
                      working={artworkOptimization.working}
                      active={artworkOptimization.active}
                      hasSourceFile={Boolean(selectedArtworkFile)}
                      onCancel={artworkOptimization.cancel}
                      onFinalize={artworkOptimization.finalize}
                      onRerun={artworkOptimization.rerun}
                      draftChanged={artworkOptimization.draftChanged}
                    />
                    {hideInlinePreview && isAbleToReplaceAssets && (
                      <div className="section-divider mb-4">
                        <h5 className="mb-3">Media replacement</h5>
                        {placeholderEdit && (
                          <p className="text-white mb-3">{PLACEHOLDER_IMPORT_MESSAGE}</p>
                        )}
                        {artworkFile.file ? (
                          <div className="d-flex flex-column gap-3">
                            {placeholderThumbnailPreview ? (
                              <img className="rounded media-preview" src={artworkFile.file} alt={`${artwork.title || "Artwork"} placeholder`} />
                            ) : null}
                            <button
                              type="button"
                              className="btn btn-outline-light align-self-start"
                              onClick={() => {
                                setArtworkFile({ file: null, type: "" });
                              }}
                            >
                              Replace media
                            </button>
                          </div>
                        ) : (
                          <UploadDropzone
                            className="upload-zone upload-zone-compact"
                            onFilesSelected={(files) => {
                              const list = Array.from(files);
                              if (list.length > 0) handleSelectedFile(list[0]);
                            }}
                            accept={replacementAccept}
                            multiple={false}
                            ariaLabel="Drop a replacement media file here to upload"
                          >
                            <div>
                              <FontAwesomeIcon
                                icon={faCloudArrowUp}
                                className="mb-2"
                                style={{ width: "32px", height: "32px", color: "var(--brand-gray)", display: "block", margin: "0 auto" }}
                              />
                              <span className="upload-label">Upload replacement media</span>
                              <p className="mt-1 mb-0 upload-subhint">
                                {artworkToEdit?.type === "3dmodel" ? "Use a .glb or .gltf file." : "Use the same artwork media type."}
                              </p>
                            </div>
                          </UploadDropzone>
                        )}
                        {getFieldError('data') && (
                          <div className="text-danger small mt-2">
                            {getFieldError('data')}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div id="description-fields-container" className="mb-1">
                        <div className="description-group mb-1">
                            <label htmlFor="description-1" className="form-label">Description</label>
                            <textarea 
                              id="description-1" 
                              name="description[]" 
                              rows={3} 
                              ref={firstDescriptionRef}
                              className={`${getFieldError('description') ? 'is-invalid form-control' : 'form-control'}`}
                              value={typeof artwork.description === "string" ? artwork.description : artwork.description[0] || ''}
                              onChange={(e) => {
                                const newDescription = typeof artwork.description === "string" ? [artwork.description] : [...artwork.description];
                                newDescription[0] = e.target.value;
                                if (showImportedMetadataEditor) {
                                  updateImportedMetadataField("shortDescription", e.target.value);
                                } else {
                                  setArtwork({ ...artwork, description: newDescription });
                                }
                                setDescriptionTouched(newDescription.map((_, index) => index === 0));
                              }}
                            ></textarea>
                            {getFieldError('description') && (
                              <div className="text-danger small mt-1">
                                {getFieldError('description')}
                              </div>
                            )}
                        </div>
                        {typeof artwork.description === 'object' && artwork.description.slice(1).map((description, index) => (
                          <div className="description-group mb-1" key={index}>
                            <div className="d-flex justify-content-end mb-1">
                              <button
                                type="button"
                                className="btn-remove-desc"
                                style={{ position: 'static' }}
                                aria-label="Remove description"
                                onClick={() => {
                                  setArtwork(prev => {
                                    const list = Array.isArray(prev.description) ? [...prev.description] : [];
                                    list.splice(index + 1, 1);
                                    return { ...prev, description: list };
                                  });
                                  setDescriptionTouched(prev => {
                                    const list = Array.isArray(prev) ? [...prev] : [];
                                    list.splice(index + 1, 1);
                                    return list;
                                  });
                                }}
                              >
                                <svg
                                  width="1em"
                                  height="1em"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                  focusable="false"
                                >
                                  <polyline points="3 6 5 6 21 6"></polyline>
                                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                                  <path d="M10 11v6"></path>
                                  <path d="M14 11v6"></path>
                                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
                                </svg>
                              </button>
                            </div>
                            <textarea 
                              id={`description-${index + 1}`}
                              name={`description[${index + 1}]`}
                              rows={3}
                              ref={(el) => {
                                extraDescriptionRefs.current[index] = el;
                              }}
                              className={`${getFieldError(`description[${index + 1}]`) ? 'is-invalid form-control' : 'form-control'}`}
                              value={description || ''}
                              onChange={(e) => {
                                const newDescription = [...artwork.description];
                                newDescription[index + 1] = e.target.value;
                                setArtwork({ ...artwork, description: newDescription });
                                setDescriptionTouched(newDescription.map((_, i) => i === index + 1));
                              }}
                            ></textarea>
                            {getFieldError(`description[${index + 1}]`) && (
                              <div className="text-danger small mt-1">
                                {getFieldError(`description[${index + 1}]`)}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>

                    
                    <button 
                      type="button" 
                      id="add-description-btn" 
                      className="btn-add-description mb-2"
                      onClick={() => {
                        setArtwork(prev => {
                          if (typeof prev.description === "string") {
                            return { ...prev, description: [prev.description, ""] };
                          } else if (typeof prev.description === "object") {
                            return { ...prev, description: [...prev.description, ""] };
                          }                          
                        });
                        setDescriptionTouched(prev => [...prev, false]);
                      }}
                    >
                        <i className="fa-solid fa-plus"></i>
                        <span>Add extra description paragraph</span>
                    </button>
                    <div className="row g-3 mb-4">
                        <div className="col-md-6" id="artwork-type-group">
                            <div>
                              <label htmlFor="artwork-height" className="form-label">Artwork Height (cm)</label>
                              <input 
                                type="number" 
                                id="artwork-height" name="artwork-height" max="999" className={`form-control ${getFieldError('height') ? 'is-invalid' : ''}`}
                                value={artwork.height}
                                onChange={(e) => {
                                  setArtwork({ ...artwork, height: e.target.value });
                                  setHeightTouched(true);
                                }}
                              />
                              {getFieldError('height') && (
                                <div className="text-danger small mt-1">
                                  {getFieldError('height')}
                                </div>
                              )}
                            </div>
                            <div>
                              <label htmlFor="artwork-type" className="form-label">Artwork Type</label>
                              <select id="artwork-type" name="artwork-type" className={`form-select ${getFieldError('artwork-type') ? 'is-invalid' : ''}`}
                                onChange={(e) => {
                                  setArtwork({ ...artwork, artworkType: e.target.value });
                                  setArtworkTypeTouched(true);
                                }}
                                value={artwork.artworkType}
                                disabled={artworkToEdit}
                              >
                                  <option value="image">image</option>
                                  <option value="video">video</option>
                                  <option value="sound">sound</option>
                                  <option value="3dmodel">3d model</option>
                                  <option value="unsupported">unsupported</option>
                              </select>
                            </div>
                            {pseudoMomLimitedMetadataEdit && showImportedMetadataEditor && (
                              <button
                                type="button"
                                className="btn-add-description mt-3 mb-0"
                                aria-expanded={showPseudoMomMetadataEditor}
                                onClick={() => setShowPseudoMomMetadataEditor((current) => !current)}
                              >
                                <i className="fa-solid fa-plus"></i>
                                <span>Edit Metadata</span>
                              </button>
                            )}
                        </div>
                        <div className="col-md-6">
                        <label htmlFor="tags" className="form-label">Tags</label>
                        <Select
                          id="tags"
                          name="tags"
                          className={`basic-multi-select ${getFieldError('tags') ? 'is-invalid' : ''}`}
                          classNamePrefix="select"
                          defaultValue={artwork.tags}
                          isMulti
                          options={ARTWORK_TAG_OPTIONS}
                          styles={{
                            ...tagStyles,
                            control: (styles, { isFocused }) => ({
                              ...styles,
                              backgroundColor: 'var(--brand-dark)',
                              color: 'var(--brand-light-gray)',
                              borderRadius: '0.375rem',
                              minHeight: '38px',
                              border: getFieldError('tags') ? '1px solid #dc3545' : 
                                     isFocused ? '1px solid var(--brand-green)' : '1px solid var(--brand-dark-3)',
                              boxShadow: isFocused ? '0 0 0 0.25rem rgba(16, 185, 129, 0.25)' : 'none',
                              '&:hover': {
                                border: getFieldError('tags') ? '1px solid #dc3545' : '1px solid var(--brand-green)'
                              }
                            }),
                            menu: (base) => ({
                              ...base,
                              backgroundColor: 'var(--brand-dark-2)',
                              color: 'var(--brand-light-gray)',
                              border: '1px solid var(--brand-dark-3)'
                            }),
                            menuList: (base) => ({
                              ...base,
                              backgroundColor: 'var(--brand-dark-2)'
                            }),
                            option: (styles, { isFocused, isSelected, isDisabled }) => ({
                              ...styles,
                              backgroundColor: isSelected
                                ? 'var(--brand-green)'
                                : isFocused
                                ? 'var(--brand-dark-3)'
                                : 'var(--brand-dark-2)',
                              color: isSelected ? '#ffffff' : 'var(--brand-light-gray)',
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                              ':active': {
                                ...styles[':active'],
                                backgroundColor: isSelected ? 'var(--brand-green)' : 'var(--brand-dark-3)'
                              }
                            }),
                            placeholder: (styles) => ({
                              ...styles,
                              color: 'var(--brand-gray)'
                            }),
                            input: (styles) => ({
                              ...styles,
                              color: 'var(--brand-light-gray)'
                            }),
                            singleValue: (styles) => ({
                              ...styles,
                              color: 'var(--brand-light-gray)'
                            }),
                            multiValue: (styles) => ({
                              ...styles,
                              backgroundColor: 'var(--brand-dark-3)'
                            }),
                            multiValueLabel: (styles) => ({
                              ...styles,
                              color: 'var(--brand-light-gray)'
                            }),
                            multiValueRemove: (styles) => ({
                              ...styles,
                              color: 'var(--brand-light-gray)',
                              ':hover': {
                                backgroundColor: 'var(--brand-dark)',
                                color: '#ffffff'
                              }
                            }),
                            dropdownIndicator: (styles) => ({
                              ...styles,
                              color: 'var(--brand-gray)',
                              ':hover': { color: 'var(--brand-light-gray)' }
                            }),
                            clearIndicator: (styles) => ({
                              ...styles,
                              color: 'var(--brand-gray)',
                              ':hover': { color: 'var(--brand-light-gray)' }
                            }),
                            indicatorSeparator: (styles) => ({
                              ...styles,
                              backgroundColor: 'var(--brand-dark-3)'
                            }),
                            valueContainer: (styles) => ({
                              ...styles,
                              color: 'var(--brand-light-gray)'
                            })
                          }}
                          onChange={(e) => {
                            onChangeTags(e);
                            setTagsTouched(true);
                          }}
                          value={artwork.tags}
                        />
                        {getFieldError('tags') && (
                          <div className="text-danger small mt-1">
                            {getFieldError('tags')}
                          </div>
                        )}
                      </div>
                    </div>
                    {shouldShowImportedMetadataFields && (
                      <div className="section-divider mb-4">
                        <h5 className="mb-3">{pseudoMomLimitedMetadataEdit ? "Metadata" : "Imported metadata"}</h5>
                        <div className="row g-3">
                          {visibleImportedMetadataFields.map((field) => {
                            const rawValue = String(((artwork as any).source?.europeanaMetadata || {})[field.key] || "");
                            const value = pseudoMomLimitedMetadataEdit
                              ? normalizePseudoMoMMetadataEditValue(field.key, rawValue, (artwork as any).edmProfile?.contextEntities)
                              : rawValue;
                            return (
                              <div className={field.multiline ? "col-12" : "col-md-6"} key={field.key}>
                                <label htmlFor={`imported-metadata-${field.key}`} className="form-label">{field.label}</label>
                                {field.multiline ? (
                                  <textarea
                                    id={`imported-metadata-${field.key}`}
                                    className="form-control"
                                    rows={3}
                                    value={value}
                                    onChange={(event) => updateImportedMetadataField(field.key, event.target.value)}
                                  />
                                ) : (
                                  <input
                                    id={`imported-metadata-${field.key}`}
                                    className="form-control"
                                    type="text"
                                    value={value}
                                    onChange={(event) => updateImportedMetadataField(field.key, event.target.value)}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {!pseudoMomLimitedMetadataEdit && <div className="section-divider">
                        <div id="digital-art-toggle-group" className="form-check mb-4">
                            <input 
                              type="checkbox" 
                              id="digital-art-checkbox" 
                              name="digital-art" 
                              className="form-check-input" 
                              checked={artwork.physical && (artwork.artworkType !== "sound" && artwork.artworkType !== "video")}
                              disabled={Boolean(artworkOptimization.job)}
                              onChange={onPhysicalArt}
                            />
                            <label htmlFor="digital-art-checkbox" className="form-check-label">Physical Artwork Properties</label>
                        </div>

                        {artwork.physical && artwork.artworkType !== "sound" && artwork.artworkType !== "video" && (
                          <>
                            <div id="select-documents-field-2" className="mb-3" ref={physicalSectionRef}>
                              <label htmlFor="select-documents-input-2" className="form-label">Authenticity documents</label>
                              <UploadDropzone
                                className="upload-zone upload-zone-compact"
                                onFilesSelected={handleSelectedAttachments}
                                multiple
                                ariaLabel="Drop documents to attach"
                              >
                                <div className="upload-label d-flex align-items-center gap-2">
                                  <FontAwesomeIcon icon={faPaperclip} />
                                  Attach files
                                </div>
                              </UploadDropzone>
                              {artwork.attachments && artwork.attachments.length > 0 && (
                                <ul className="mt-2 mb-0" style={{ listStyle: "none", paddingLeft: 0 }}>
                                  {artwork.attachments.map((file, i) => (
                                    <li key={i} className="d-flex align-items-center py-1">
                                      <span className="text-truncate text-white attachment-name" title={file.name}>{file.name}</span>
                                      <button
                                        className="ms-3 attachment-remove-btn"
                                        type="button"
                                        aria-label="Remove document"
                                        onClick={() => removeAttachmentAtIndex(i)}
                                      >
                                        <FontAwesomeIcon icon={faTrashCan} />
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>

                            <div className="row g-3">
                              <div className="col-md-6" id="material-group-new">
                                <label htmlFor="material-new" className="form-label">Material</label>
                                <input 
                                  type="text" 
                                  id="material-new" 
                                  name="material" 
                                  className={`form-control ${getFieldError('materials') ? 'is-invalid' : ''}`} 
                                  value={artwork.materials}
                                  onChange={(e) => {
                                    setArtwork({ ...artwork, materials: e.target.value });
                                    setMaterialsTouched(true);
                                  }}
                                />
                              </div>
                              <div className="col-md-6" id="date-creation-group-new">
                                <label htmlFor="date-of-creation-new" className="form-label">Date of Creation</label>
                                <input 
                                  type="date" 
                                  id="date-of-creation-new" 
                                  name="date-of-creation" 
                                  className={`form-control ${getFieldError('dateofcreation') ? 'is-invalid' : ''}`}
                                  value={artwork.dateofcreation}
                                  onChange={(e) => {
                                    setArtwork({ ...artwork, dateofcreation: e.target.value });
                                    setDateofcreationTouched(true);
                                  }}
                                />
                              </div>
                            </div>
                          </>
                        )}
                    </div>}
                </form>
          </div>
          <div id="modal-footer" className="modal-footer-custom">
            {!inline ? (
              <button id="exit-button-footer" className="btn-exit" onClick={() => onCreatedHandler(false, undefined)}>Go Back</button>
            ) : null}
            <button 
              id="create-button" 
              className="btn-create" 
              disabled={submissionWorking || artworkOptimization.active || artworkOptimization.awaitingDecision}
              onClick={createArtwork}
            >{submissionWorking ? "Working..." : submitLabel}</button>
          </div>
        </div>
  );

  return (
    <>
    {inline ? (
      <div className={`new-artwork-inline ${inlinePresentation === "modal" ? "new-artwork-inline-modal" : ""}`}>
        {formContent}
      </div>
    ) : (
      <Modal
        show={show}
        onHide={() => onCreatedHandler(edited, undefined)}
        size="lg"
        centered
        backdrop="static"
        contentClassName="modal-no-padding new-artwork-rounded new-artwork-scroll"
      >
        <Modal.Body>
          {formContent}
        </Modal.Body>
      </Modal>
    )}
    
    <ConfirmModal
      show={showConfirm}
      onHide={() => setShowConfirm(false)}
      onConfirm={submitArtwork}
      title="Confirm Create Artwork"
      message="We cannot confirm your artwork file type. Do you really want to create this artwork?"
      confirmText="Yes, Create"
      cancelText="No"
    />
    
    </>
  );
};
